
import { defineComponent, ref, reactive, onBeforeUnmount, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { getSvgIconDataUrl } from '@/utils/svg-icon-data-url'
import { User, UserInfo } from '@/services/user'
import { Team, TeamInfo } from '@/services/team'

import { ACTIVE_NAME_MAP, LeaveParams, TeamManageService } from './team-manage.service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'

import ContentAdaptLayout from '@/views/dashboard/_components/content-adapt-layout/content-adapt-layout.vue'
import { useRouter } from 'vue-router'
import TeamList from '@/views/team/_components/team-list/team-list.vue'
import { TeamListShowType } from '@/views/team/_components/team-list/team-list.service'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'

const TEAM_COMPANY_SELECTED_ICON = 'views/dashboard/_components/central-control/_components/team-manage/_images/company-selected'
const TEAM_SWITCH_BADGE_ICON = 'views/dashboard/_components/central-control/_components/team-manage/_images/switch'

export default defineComponent({
  components: {
    TeamList,
    ContentAdaptLayout
  },
  emits: ['close', 'createTeam'],
  setup (_, context) {
    const { t } = useI18n()
    const injector = useReflectiveInjector([TeamManageService])
    const user = injector.get(User)
    const userInfo = reactive<Partial<UserInfo>>({})
    const router = useRouter()

    const team = injector.get(Team)
    const teamList = ref<TeamInfo[]>([])
    const currentTeam = ref<TeamInfo>()

    team.teamList.subscribe(list => {
      teamList.value = list
    })

    team.currentTeam.subscribe(team => {
      currentTeam.value = team
    })

    const activeName = ref<string>(ACTIVE_NAME_MAP.default)
    const activeTeam = reactive<Partial<TeamInfo>>({})

    const leaveParams = reactive<Partial<LeaveParams>>({})
    const teamManageService = injector.get(TeamManageService)

    const MAX_COUNTDOWN = 10
    const ONE_SECOND_MS = 1000
    const countDown = ref<number>(MAX_COUNTDOWN)
    const countDownTimer = ref(null)

    function runLeaveCountDown () {
      clearInterval(countDownTimer.value)
      countDown.value = MAX_COUNTDOWN
      countDownTimer.value = setInterval(() => {
        countDown.value--
        if (!countDown.value) {
          clearInterval(countDownTimer.value)
        }
      }, ONE_SECOND_MS)
    }

    user.userInfo.subscribe(info => {
      Object.assign(userInfo, info)
      leaveParams.target_uid = userInfo.uid
    })

    onBeforeUnmount(() => {
      clearInterval(countDownTimer.value)
    })

    teamManageService.getTeamList()

    const leaveTeamDisabledTip = computed(() =>
      t('teamManage.leaveTeamDisabledTip', { role: activeTeam.role_name ?? '' })
    )

    return {
      t,IS_JP_ONLY_BUILD,
      companySelectedIconUrl: getSvgIconDataUrl(TEAM_COMPANY_SELECTED_ICON),
      switchBadgeIconUrl: getSvgIconDataUrl(TEAM_SWITCH_BADGE_ICON),
      leaveTeamDisabledTip,
      ACTIVE_NAME_MAP,
      TeamListShowType,
      teamList,
      currentTeam,
      activeName,
      activeTeam,
      countDown,
      handlerViewTeam (item: TeamInfo) {
        activeName.value = ACTIVE_NAME_MAP.viewTeam
        Object.assign(activeTeam, item)
      },
      handlerCancelViewTeam () {
        activeName.value = ACTIVE_NAME_MAP.default
      },
      handlerSwitchTeam () {
        teamManageService.doSwitchTeam(activeTeam as TeamInfo).then(() => {
          router.push('/dashboard')
          context.emit('close')
        })
      },
      handlerShowLeaveTeam () {
        activeName.value = ACTIVE_NAME_MAP.leaveTeam
        runLeaveCountDown()
        leaveParams.team_id = activeTeam.team_id
      },
      handleCancelLeaveTeam () {
        activeName.value = ACTIVE_NAME_MAP.viewTeam
      },
      handleLeaveTeam () {
        teamManageService.doLeaveTeam({
          team_id: leaveParams.team_id,
          target_uid: leaveParams.target_uid
        }).then(() => {
          activeName.value = ACTIVE_NAME_MAP.default
        })
      },
      handlerCreateTeam () {
        context.emit('createTeam')
      },
    }
  }
})
